import { useCallback, useEffect, useState } from 'react';

const STEP_SETTLE_MS = 350;

/**
 * Owns navigation state for a multi-section hobby form.
 * `isStepSettled` prevents an immediate save from sharing the same click as a step change.
 */
export default function useStepperFlow(steps, initialSection) {
  const [activeSection, setActiveSection] = useState(initialSection);
  const [isStepSettled, setIsStepSettled] = useState(true);
  const activeStepIndex = Math.max(steps.indexOf(activeSection), 0);
  const canGoBack = activeStepIndex > 0;
  const canGoNext = activeStepIndex < steps.length - 1;

  const goToSection = useCallback((section) => {
    if (!steps.includes(section)) return;
    setActiveSection(section);
    setIsStepSettled(false);
  }, [steps]);

  const goToPreviousStep = useCallback(() => {
    if (!canGoBack) return;
    goToSection(steps[activeStepIndex - 1]);
  }, [activeStepIndex, canGoBack, goToSection, steps]);

  const goToNextStep = useCallback(() => {
    if (!canGoNext) return;
    goToSection(steps[activeStepIndex + 1]);
  }, [activeStepIndex, canGoNext, goToSection, steps]);

  const resetStepper = useCallback((section = initialSection) => {
    setActiveSection(section);
    setIsStepSettled(true);
  }, [initialSection]);

  useEffect(() => {
    if (isStepSettled) return undefined;
    const timer = window.setTimeout(() => setIsStepSettled(true), STEP_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [isStepSettled, activeSection]);

  return {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  };
}
